import {
  type FC, useCallback, useEffect, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { type ModalProps } from '~/modal/modalType.enum';
import { BoundaryFillSettingsLayout } from '~/sidebar/sidebarApps/mapTools/boundary/customizeFills/boundaryFillSettingsContentComponent';
import {
  useBoundaryGroupPermissions,
  useBoundaryGroupSelector,
  useBoundaryGroupsIsListingLoadingSelector,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useBoundaryTerritoryDetailsIsLoadingSelector } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import {
  boundaryTerritoryGroupsBulkSaveRequest,
  boundaryTerritoryGroupsUpdateRequest,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  useBoundaryTerritoryGroupsGroupedByBoundaryGroupSelector,
  useBoundaryTerritoryGroupsIsLoadingSelector, useGetBoundaryTerritoryGroup,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { type BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { BoundarySettingsModalDialogComponent } from './boundarySettingsModalDialogComponent';
import {
  type BoundarySettingsPropsVariantsArguments,
  useBoundaryFillSettingsPropsVariants,
} from './useBoundaryFillSettingsPropsVariants';

export type BoundarySettingsContainerProps = ModalProps<{
  boundaryTerritoryGroupId: number;
}>;

const areMatchingsEqual = (a: BoundaryTerritoryGroup['matchings'], b: BoundaryTerritoryGroup['matchings']): boolean => {
  if (a.matchingType !== b.matchingType) {
    return false;
  }
  const columnsA = a.matchColumns.map(column => column.columnId);
  const columnsB = b.matchColumns.map(column => column.columnId);
  if (columnsA.length !== columnsB.length) {
    return false;
  }
  return columnsA.every((column, index) => column === columnsB[index]);
};

export const BoundarySettingsContainer: FC<BoundarySettingsContainerProps> = ({
  boundaryTerritoryGroupId,
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const isBoundaryTerritoryGroupLoading = useBoundaryTerritoryGroupsIsLoadingSelector();
  const isBoundaryGroupsListingLoading = useBoundaryGroupsIsListingLoadingSelector();
  const isBoundaryTerritoryDetailsLoading = useBoundaryTerritoryDetailsIsLoadingSelector();
  const { getBoundaryTerritoryGroupById } = useGetBoundaryTerritoryGroup();
  const BTGsGroupedByBoundaryGroupId = useBoundaryTerritoryGroupsGroupedByBoundaryGroupSelector();
  const originalBTG = getBoundaryTerritoryGroupById(boundaryTerritoryGroupId);
  const [boundaryTerritoryGroup, setBoundaryTerritoryGroup] = useState(originalBTG);
  const [valueIsInitial, setValueIsInitial] = useState(true);

  const boundaryGroup = useBoundaryGroupSelector(boundaryTerritoryGroup?.boundaryGroupId);
  const boundaryPermissions = useBoundaryGroupPermissions(boundaryTerritoryGroup?.boundaryGroupId);

  const updateBoundaryTerritoryGroup = useCallback<BoundarySettingsPropsVariantsArguments['onBoundaryTerritoryGroupUpdate']>(
    (updated: BoundaryTerritoryGroup, options?: { updateIsReset?: boolean }) => {
      setValueIsInitial(!!options?.updateIsReset);
      setBoundaryTerritoryGroup(updated);
    }, [],
  );

  const boundaryTerritoryGroupProps = useBoundaryFillSettingsPropsVariants({
    boundaryTerritoryGroup,
    onBoundaryTerritoryGroupUpdate: updateBoundaryTerritoryGroup,
    onIsLoadingChange: setIsLoading,
  });
  const boundaryTerritoryGroupPropsLayout = boundaryTerritoryGroupProps?.layout;
  const handleBoundaryGroupNameChange = boundaryTerritoryGroupPropsLayout === BoundaryFillSettingsLayout.Group ?
    boundaryTerritoryGroupProps?.onBoundaryGroupNameChange :
    null;

  const initialBoundaryGroupName = boundaryTerritoryGroupProps?.layout === BoundaryFillSettingsLayout.Group ?
    boundaryTerritoryGroupProps?.boundaryGroupName :
    '';
  const [boundaryGroupName, setBoundaryGroupName] = useState<string>(initialBoundaryGroupName);

  const hasMatchingChanged = (!!boundaryTerritoryGroup && !!originalBTG) && !areMatchingsEqual(
    originalBTG.matchings,
    boundaryTerritoryGroup.matchings,
  );

  const submit = useCallback(() => {
    if (!boundaryTerritoryGroup) {
      return;
    }

    if ((boundaryTerritoryGroupPropsLayout === BoundaryFillSettingsLayout.Group) && (initialBoundaryGroupName !== boundaryGroupName)) {
      handleBoundaryGroupNameChange?.(boundaryGroupName);
    }
    if (hasMatchingChanged) {
      const allFillsForEditedBoundaryGroup = notNullsy(boundaryTerritoryGroup?.boundaryGroupId)
        ? BTGsGroupedByBoundaryGroupId.find(btgInfo => btgInfo.boundaryGroupId === boundaryTerritoryGroup.boundaryGroupId)?.boundaryTerritoryGroups
        : undefined;

      if (allFillsForEditedBoundaryGroup) {
        const BTGsToUpdate: readonly BoundaryTerritoryGroup[] = allFillsForEditedBoundaryGroup.map(fill => {
          if (!fill) {
            return null;
          }
          if (fill.boundaryTerritoryGroupId === boundaryTerritoryGroup.boundaryTerritoryGroupId) {
            return boundaryTerritoryGroup;
          }
          return {
            ...fill,
            matchings: boundaryTerritoryGroup.matchings,
          } satisfies BoundaryTerritoryGroup;
        }).filter(notNullsy);

        dispatch(boundaryTerritoryGroupsBulkSaveRequest(BTGsToUpdate, { onSuccess: onClose }));
      }
      else {
        throw new Error('Could not load boundary territory group fills.');
      }
    }
    else {
      dispatch(boundaryTerritoryGroupsUpdateRequest(boundaryTerritoryGroup, { onDone: onClose }));
    }
  },
  [BTGsGroupedByBoundaryGroupId, boundaryGroupName, boundaryTerritoryGroup, boundaryTerritoryGroupPropsLayout, dispatch, handleBoundaryGroupNameChange, hasMatchingChanged, initialBoundaryGroupName, onClose]);

  // update local boundary territory group instance if was changed in the store (might happen after concurrency error)
  useEffect(() => {
    const BTGFromState = getBoundaryTerritoryGroupById(boundaryTerritoryGroupId);
    if (boundaryTerritoryGroup && BTGFromState &&
      (boundaryTerritoryGroup.etag?.timestamp !== BTGFromState.etag?.timestamp)) {
      setBoundaryTerritoryGroup(BTGFromState);
    }
  }, [boundaryTerritoryGroup, boundaryTerritoryGroupId, getBoundaryTerritoryGroupById]);

  if (!boundaryTerritoryGroup || !boundaryTerritoryGroupProps || !boundaryGroup) {
    return null;
  }
  const restOfBoundarySettingsComponentProps = {
    ...boundaryTerritoryGroupProps,
    onBoundaryGroupNameChange: boundaryPermissions.boundaryIsEditable ? setBoundaryGroupName : undefined,
  };

  return (
    <BoundarySettingsModalDialogComponent
      boundaryGroup={boundaryGroup}
      boundaryTerritoryGroup={boundaryTerritoryGroup}
      boundaryPermissions={boundaryPermissions}
      valueIsInitial={valueIsInitial}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      isLoading={isLoading || isBoundaryTerritoryGroupLoading || isBoundaryTerritoryDetailsLoading || isBoundaryGroupsListingLoading}
      onBoundaryTerritoryGroupChange={updateBoundaryTerritoryGroup}
      {...restOfBoundarySettingsComponentProps}
    />
  );
};
